import { props } from "ramda";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Card, LoadingSpinner } from "widgets";
import { useURLSearchParam } from "widgets/hooks";

import { prjCommentingAllowed } from "../../app-utils/capability.js";
import { getTopPosition } from "../../app-utils/vertical-state.js";
import { type Project, type ProjectActivity, type User } from "../../graphql.js";
import {
  loadProject,
  loadProjectActivities,
  postComment,
  unwatchProjectActivity,
  watchProjectActivity,
} from "../../redux/modules/project.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { MAX_LENGTH } from "../../settings/settings.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import CommentInputForm from "./CommentInputForm.js";
import { ProjectActivityStream } from "./ProjectActivityStream.js";
import { CLEAN_PROJECT_ACTIVITIES } from "@/redux/action-types.js";
import { ScrollTarget } from "@/ui-modules/projects/utils/scrollTarget.js";

/**
 * The panel for the project activities of one project on the right in project overview.
 *   Contains the search field, the stack of activities and the "New Comment" field,
 */

export const ProjectActivityPanel: FC<{ projectId: number }> = ({ projectId }) => {
  const dispatch = useDispatch<ReduxDispatch>();

  // depends on the scroll-spy, automatic scroll will happen only if last activity is visible
  // true if user is inspecting activities (latest activity not visible) ; simply ref because the change of this value should not trigger a refresh
  const scrollToBottomDisabled = useRef<boolean>(false);

  // the value of filterExpression must not become null or undefined! see controlled vs. uncontrolled FormControl
  const [filterExpression, setFilterExpression] = useState<string>();
  const [termList, setTermList] = useState<string[]>([]);
  const [scrollTarget, setScrollTarget] = useState<ScrollTarget>(ScrollTarget.noTarget);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activityId = useURLSearchParam("activityid");
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);

  const [isLoading, setIsLoading] = useState(false);
  const project = useSelector<IAppState, Project>((state) => state.project.project);
  const projectActivities = useSelector<IAppState, ProjectActivity[]>((state) => state.project.projectActivities);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      if (project == null || project.id !== projectId) {
        await dispatch(loadProject(projectId));
      }
      await dispatch(loadProjectActivities(projectId));
      setIsLoading(false);
    }

    void load();

    return () => {
      dispatch({ type: CLEAN_PROJECT_ACTIVITIES });
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const activitySubscriptionId = dispatch(watchProjectActivity(projectId));

    return () => dispatch(unwatchProjectActivity(activitySubscriptionId));
  }, [projectId]);

  useEffect(() => {
    if (projectId && projectActivities) {
      if (activityId) {
        setScrollTarget(ScrollTarget.toId);
      } else if (scrollTarget === ScrollTarget.noTarget) {
        setScrollTarget(ScrollTarget.toInitialLatest);
      }
    }
  }, [projectId, projectActivities]);

  // if pointer (query param) to activityId changes then we scroll to the related activity
  useEffect(() => {
    if (activityId) {
      setScrollTarget(ScrollTarget.toId);
    }
  }, [activityId]);

  // new activity enters the pipeline, we should make it visible by automatically scrolling down
  useEffect(() => {
    if (
      projectActivities?.length &&
      scrollTarget !== ScrollTarget.toId &&
      scrollTarget !== ScrollTarget.toInitialLatest
    ) {
      const latestActivity = projectActivities?.[0];
      const isSelfActivity = latestActivity?.user?.username === currentUser?.username;
      // ...unless the user scrolled up to inspect some activities
      if (!scrollToBottomDisabled.current || isSelfActivity) {
        setScrollTarget(ScrollTarget.toLatest);
      }
    }
  }, [projectActivities?.length]);

  useEffect(() => {
    const applyScroll = () => {
      const scrollContainer = scrollRef.current;
      const element = document.getElementById(activityId);
      const top =
        element && scrollContainer
          ? getTopPosition(element, scrollContainer) - 100
          : (scrollContainer?.scrollHeight ?? 0);

      switch (scrollTarget) {
        case ScrollTarget.toId:
          scrollContainer?.scrollTo({ top, behavior: "smooth" });
          break;
        case ScrollTarget.toLatest:
          scrollContainer?.scrollTo({ top: scrollContainer?.scrollHeight, behavior: "smooth" });
          break;
        case ScrollTarget.toInitialLatest:
          scrollContainer?.scrollTo({ top: scrollContainer?.scrollHeight });
          break;
        default:
          break;
      }

      setScrollTarget(ScrollTarget.noTarget);
    };

    if (scrollTarget !== ScrollTarget.noTarget && !isLoading) {
      setTimeout(applyScroll, 300);
    }
  }, [scrollTarget, isLoading]);

  const enableScrollToBottom = useCallback(() => (scrollToBottomDisabled.current = false), []);
  const disableScrollToBottom = useCallback(() => (scrollToBottomDisabled.current = true), []);

  return (
    <Card
      title="Activity"
      actions={[
        <AsyncSearchWithExprParser
          onSearchExprChange={async (expr, terms) => {
            setFilterExpression(expr);
            setTermList(terms);
          }}
          key={"project-activity-panel-1"}
        />,
      ]}
      noPadding
      fillHeight
      style={{
        borderWidth: 0,
        borderRadius: 0,
        minWidth: 150, // Needed to keep the scroll
      }}
      className="project-conversations"
    >
      <span id="project-activity-panel-title" className="hotspot-beacon" />
      {/* For scrolling to an activity the containerElement must be positioned !
          CSS style has to set position: 'relative' or 'absolute' and overflowY: 'scroll' or 'auto'*/}
      <div
        // name={"projectActivityPanelContainerElement"}
        className="activities-panel-body"
        id="containerElement"
        style={{
          position: "relative",
          flex: "1 0 0",
          marginBottom: -15,
          // overflowY: "scroll", // already in CSS
        }}
        ref={scrollRef}
      >
        <div className="conversation-list-wrapper" style={{ overflowY: "hidden" }}>
          {isLoading ? (
            <LoadingSpinner inCenter={true} />
          ) : (
            <ProjectActivityStream
              activities={projectActivities}
              filterExpression={filterExpression}
              filterTermList={termList}
              scrollTargetToId={scrollTarget === ScrollTarget.toId}
              enableScrollToBottom={enableScrollToBottom}
              disableScrollToBottom={disableScrollToBottom}
            />
          )}
        </div>
      </div>

      {project && prjCommentingAllowed(project) && (
        <div className="panel-conversation-footer" style={{ marginTop: "auto" }}>
          <CommentInputForm
            onPostComment={(comment, groupIds, viewIds, flagIds, projectFileIds) =>
              dispatch(postComment(project.id, comment, groupIds, viewIds, flagIds, projectFileIds))
            }
            project={project}
            projectIsLoading={isLoading}
            currentUser={currentUser}
            buttonLabel={"Post"}
            fieldLabel={"Comment"}
            fieldPlaceholder={"Type comment here"}
            maxChars={MAX_LENGTH.PROJECT.COMMENT}
          />
        </div>
      )}
    </Card>
  );
};
